<template>
  <el-dialog
    :model-value="modelValue"
    :title="title"
    width="min(520px, calc(100vw - 24px))"
    class="device-management-dialog"
    :close-on-click-modal="!busy"
    :close-on-press-escape="!busy"
    :show-close="!busy"
    destroy-on-close
    @update:model-value="close"
  >
    <div class="management-body">
      <template v-if="panel === 'manage' && device">
        <header class="managed-device">
          <el-icon class="device-symbol"><Cellphone /></el-icon>
          <div class="managed-identity">
            <h2>{{ device.deviceName || '云手机' }}</h2>
            <p>编码：{{ device.deviceCode || device.deviceId }}</p>
          </div>
          <el-tooltip content="修改云机名称" :enterable="false">
            <el-button :icon="EditPen" circle text aria-label="修改云机名称" @click="openRename" />
          </el-tooltip>
        </header>
        <div class="device-expiry">
          <span>到期时间 {{ formattedExpiry }}</span>
          <strong :class="{ urgent: expiryUrgent }">{{ remainingTime }}</strong>
        </div>
        <p v-if="recoveryMessage" class="recovery-message" role="status">{{ recoveryMessage }}</p>
        <section class="management-section">
          <h3>续费设备</h3>
          <div class="management-actions">
            <button type="button" disabled><el-icon><ShoppingCart /></el-icon><span>购买续费</span><small>即将开放</small></button>
            <button type="button" class="accent-blue" @click="openRedeem(device.deviceId)"><el-icon><Ticket /></el-icon><span>激活码续费</span></button>
            <button type="button" disabled><el-icon><Star /></el-icon><span>积分续费</span><small>即将开放</small></button>
          </div>
        </section>
        <section class="management-section">
          <h3>云机操作</h3>
          <div class="management-actions">
            <button type="button" :disabled="!canOperate" @click="showPanel('restart')"><el-icon><RefreshRight /></el-icon><span>重启</span></button>
            <button type="button" :disabled="!canOperate" class="danger-action" @click="showPanel('reset')"><el-icon><RefreshLeft /></el-icon><span>恢复出厂</span></button>
            <button type="button" :disabled="!canOperate" class="accent-teal" @click="requestUpload"><el-icon><UploadFilled /></el-icon><span>文件上传</span></button>
          </div>
          <p v-if="device.deviceStatus !== 'ONLINE'" class="operation-note">设备离线，暂不可执行云机操作</p>
        </section>
      </template>

      <div v-else-if="panel === 'new'" class="new-device-options">
        <button type="button" disabled><el-icon><ShoppingCart /></el-icon><span>购买云手机</span><small>即将开放</small></button>
        <button type="button" @click="openRedeem(null)"><el-icon><Ticket /></el-icon><span>激活码兑换</span><el-icon><ArrowRight /></el-icon></button>
        <button type="button" disabled><el-icon><Star /></el-icon><span>积分兑换</span><small>即将开放</small></button>
      </div>

      <form v-else id="device-management-form" class="management-form" @submit.prevent="submit">
        <template v-if="panel === 'rename'">
          <label for="cloud-device-name">云机名称</label>
          <input id="cloud-device-name" v-model="newName" :disabled="busy" maxlength="30" autocomplete="off" @input="errorMessage = ''">
          <span class="field-note">{{ newName.length }}/30</span>
        </template>
        <template v-else-if="panel === 'redeem'">
          <label for="cloud-activation-code">激活码</label>
          <input id="cloud-activation-code" v-model="code" :disabled="busy" autocomplete="off" autocapitalize="characters" :spellcheck="false" @input="errorMessage = ''">
          <label for="cloud-redeem-target">兑换对象</label>
          <select id="cloud-redeem-target" v-model="redeemTarget" :disabled="busy" @change="errorMessage = ''">
            <option value="">领取新云机</option>
            <option v-for="item in devices" :key="item.deviceId" :value="String(item.deviceId)">续期：{{ item.deviceName || `云手机 ${item.deviceId}` }}</option>
          </select>
        </template>
        <template v-else-if="panel === 'restart' || panel === 'reset'">
          <p class="confirmation-device">{{ device.deviceName || '云手机' }}<span>编码：{{ device.deviceCode || device.deviceId }}</span></p>
          <p v-if="panel === 'restart'" class="confirmation-note">重启将中断当前投屏，云机内正在运行的任务可能受到影响。</p>
          <template v-else>
            <p class="confirmation-note danger-text">恢复出厂会清空这台云手机内的应用、文件和个人数据，无法撤销。</p>
            <label class="reset-consent"><input v-model="resetConfirmed" type="checkbox" :disabled="busy"><span>我确认清空这台云手机的数据</span></label>
          </template>
        </template>
        <p v-if="errorMessage" class="management-error" role="alert">{{ errorMessage }}</p>
      </form>
    </div>
    <template #footer>
      <template v-if="panel === 'manage' || panel === 'new'"><el-button @click="close">关闭</el-button></template>
      <template v-else>
        <el-button :disabled="busy" :icon="ArrowLeft" @click="showPanel(device ? 'manage' : 'new')">返回</el-button>
        <el-button :type="panel === 'reset' ? 'danger' : 'primary'" :loading="busy" :disabled="!canSubmit || busy" native-type="submit" form="device-management-form">{{ submitLabel }}</el-button>
      </template>
    </template>
  </el-dialog>
</template>

<script setup>
/* global defineEmits, defineProps */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { ArrowLeft, ArrowRight, Cellphone, EditPen, RefreshLeft, RefreshRight, ShoppingCart, Star, Ticket, UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { redeemActivationCode, renameCloudDevice, resetCloudDevice, restartCloudDevice } from '../../api/cloudDevice'
import { getRequestErrorMessage } from '../../api/http'

const props = defineProps({
  modelValue: Boolean,
  device: { type: Object, default: null },
  devices: { type: Array, default: () => [] },
  recoveryMessage: { type: String, default: '' }
})
const emit = defineEmits(['update:modelValue', 'renamed', 'redeemed', 'lifecycle', 'upload'])
const panel = ref('new')
const busy = ref(false)
const newName = ref('')
const code = ref('')
const redeemTarget = ref('')
const resetConfirmed = ref(false)
const errorMessage = ref('')
const now = ref(Date.now())
let clockTimer = null
let disposed = false

const titles = { manage: '云机设置', new: '新增云手机', rename: '修改云机名称', redeem: '兑换激活码', restart: '重启云手机', reset: '恢复出厂设置' }
const title = computed(() => titles[panel.value])
const formattedExpiry = computed(() => props.device?.expireTime?.replace('T', ' ').slice(0, 16) || '未设置')
const remainingSeconds = computed(() => {
  const time = props.device?.expireTime
  return time ? (new Date(time.replace(' ', 'T')).getTime() - now.value) / 1000 : NaN
})
const expiryUrgent = computed(() => Number.isFinite(remainingSeconds.value) && remainingSeconds.value <= 86400)
const remainingTime = computed(() => {
  const seconds = remainingSeconds.value
  if (!Number.isFinite(seconds)) return '剩余时间未知'
  if (seconds <= 0) return '已到期'
  return seconds <= 86400 ? `剩余 ${Math.ceil(seconds / 3600)} 小时` : `剩余 ${Math.floor(seconds / 86400)} 天`
})
const canOperate = computed(() => props.device?.deviceStatus === 'ONLINE' && !props.recoveryMessage)
const canSubmit = computed(() => {
  if (panel.value === 'rename') return newName.value.trim().length > 0 && newName.value.trim().length <= 30
  if (panel.value === 'redeem') return Boolean(code.value.trim()) && (!redeemTarget.value || props.devices.some(item => String(item.deviceId) === redeemTarget.value))
  if (panel.value === 'restart') return canOperate.value
  if (panel.value === 'reset') return canOperate.value && resetConfirmed.value
  return false
})
const submitLabel = computed(() => ({ rename: '保存名称', redeem: '确认兑换', restart: '确认重启', reset: '确认恢复出厂' }[panel.value]))

watch(() => props.modelValue, visible => {
  window.clearInterval(clockTimer)
  if (!visible) return
  panel.value = props.device ? 'manage' : 'new'
  errorMessage.value = ''
  code.value = ''
  resetConfirmed.value = false
  now.value = Date.now()
  clockTimer = window.setInterval(() => { now.value = Date.now() }, 60000)
}, { immediate: true })

function close() {
  if (!busy.value) emit('update:modelValue', false)
}

function showPanel(nextPanel) {
  if (busy.value) return
  panel.value = nextPanel
  errorMessage.value = ''
  resetConfirmed.value = false
}

function openRename() {
  newName.value = props.device.deviceName || ''
  showPanel('rename')
}

function openRedeem(deviceId) {
  code.value = ''
  redeemTarget.value = deviceId == null ? '' : String(deviceId)
  showPanel('redeem')
}

function requestUpload() {
  if (!canOperate.value) return
  emit('upload', props.device)
  close()
}

async function submit() {
  if (busy.value || !canSubmit.value) return
  busy.value = true
  errorMessage.value = ''
  const action = panel.value
  const deviceId = props.device?.deviceId
  try {
    if (action === 'rename') {
      const deviceName = newName.value.trim()
      await renameCloudDevice(deviceId, deviceName)
      if (disposed) return
      emit('renamed', { deviceId, deviceName })
      ElMessage.success('名称已更新')
    } else if (action === 'redeem') {
      const target = props.devices.find(item => String(item.deviceId) === redeemTarget.value)
      await redeemActivationCode(code.value.trim(), redeemTarget.value ? target.deviceId : null)
      if (disposed) return
      emit('redeemed', { isNew: !redeemTarget.value })
      ElMessage.success(redeemTarget.value ? '续费成功' : '兑换成功')
    } else {
      await (action === 'restart' ? restartCloudDevice(deviceId) : resetCloudDevice(deviceId))
      if (disposed) return
      emit('lifecycle', { deviceId, action })
      ElMessage.success(action === 'restart' ? '重启请求已提交' : '恢复出厂请求已提交')
    }
    emit('update:modelValue', false)
  } catch (error) {
    if (!disposed) {
      // A timed-out mutation may already have succeeded; never retry it silently.
      errorMessage.value = !error.response && error.code
        ? '请求结果未确认，请刷新设备列表核实后再操作'
        : getRequestErrorMessage(error, '操作失败，请稍后重试')
    }
  } finally {
    busy.value = false
  }
}

onBeforeUnmount(() => {
  disposed = true
  window.clearInterval(clockTimer)
})
</script>

<style scoped>
.management-body { min-width: 0; color: #303a3b; }
.managed-device { display: flex; align-items: center; gap: 10px; }
.device-symbol { flex-shrink: 0; width: 44px; height: 44px; font-size: 25px; color: #3877eb; background: #e7eeff; border-radius: 8px; }
.managed-identity { flex: 1; min-width: 0; }
.managed-identity h2 { margin: 0; font-size: 18px; line-height: 1.4; overflow-wrap: anywhere; }
.managed-identity p { margin: 5px 0 0; color: #737e83; font-size: 12px; overflow-wrap: anywhere; }
.managed-device .el-button { flex-shrink: 0; }
.device-expiry { display: flex; flex-wrap: wrap; gap: 8px 16px; margin: 16px 0; font-size: 12px; color: #737e83; }
.device-expiry strong { color: #049b97; font-weight: 500; }
.device-expiry .urgent, .danger-text { color: #c93949; }
.management-section { padding: 16px 0 6px; border-top: 1px solid #edf0f0; }
.management-section h3 { margin: 0 0 10px; font-size: 14px; font-weight: 600; }
.management-actions { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.management-actions button { display: flex; flex-direction: column; align-items: center; gap: 7px; min-width: 0; min-height: 82px; padding: 10px 2px; color: #3d4548; font: inherit; font-size: 13px; background: transparent; border: 0; border-radius: 6px; cursor: pointer; }
.management-actions .el-icon { font-size: 24px; }
.management-actions button:hover:not(:disabled), .new-device-options button:hover:not(:disabled) { background: #edf9f7; }
.management-actions button:disabled, .new-device-options button:disabled { color: #a1a8ac; cursor: not-allowed; }
.management-actions small { font-size: 11px; }
.management-actions .accent-blue:not(:disabled) .el-icon { color: #3979ef; }
.management-actions .accent-teal:not(:disabled) .el-icon { color: #049b97; }
.management-actions .danger-action:not(:disabled) .el-icon { color: #c93949; }
.new-device-options { display: flex; flex-direction: column; }
.new-device-options button { display: flex; align-items: center; gap: 12px; min-height: 64px; padding: 12px 8px; color: #047f7b; font: inherit; background: none; border: 0; border-bottom: 1px solid #edf0f0; cursor: pointer; }
.new-device-options .el-icon { flex-shrink: 0; font-size: 23px; }
.new-device-options span { flex: 1; text-align: left; }
.new-device-options small { font-size: 12px; }
.management-form { display: flex; flex-direction: column; gap: 10px; }
.management-form > label { font-size: 14px; }
.management-form > input, .management-form > select { box-sizing: border-box; width: 100%; min-width: 0; min-height: 46px; padding: 10px 12px; border: 1px solid #cedad8; border-radius: 6px; color: #253a38; font: inherit; font-size: 16px; background: #fff; }
.management-form > input:focus, .management-form > select:focus { outline: 2px solid #049b97; outline-offset: 1px; }
.field-note { align-self: flex-end; font-size: 12px; color: #737e83; }
.management-error { margin: 4px 0; color: #c93949; overflow-wrap: anywhere; }
.confirmation-device { margin: 0; font-size: 17px; font-weight: 600; overflow-wrap: anywhere; }
.confirmation-device span { display: block; margin-top: 6px; color: #737e83; font-size: 12px; font-weight: 400; }
.confirmation-note { margin: 8px 0; line-height: 1.7; }
.reset-consent { display: flex; align-items: flex-start; gap: 8px; line-height: 1.6; }
.reset-consent input { flex-shrink: 0; width: 18px; height: 18px; margin: 2px 0 0; accent-color: #c93949; }
.operation-note, .recovery-message { color: #7a8085; font-size: 12px; line-height: 1.6; }
:global(.device-management-dialog) { --el-dialog-margin-top: 5vh; display: flex; flex-direction: column; max-height: 90vh; max-height: 90dvh; border-radius: 8px; }
:global(.device-management-dialog .el-dialog__body) { min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
:global(.device-management-dialog .el-dialog__header), :global(.device-management-dialog .el-dialog__footer) { flex-shrink: 0; }
</style>
